import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * BroadcastChannel-based sync between presenter and audience windows.
 *
 * The presenter window is the "driver" — it sends slide index changes.
 * The audience window listens and follows.
 *
 * Channel name is scoped to the deck slug so multiple decks can run independently.
 */

const CHANNEL_PREFIX = 'sn-presenter-';

export type PresenterRole = 'presenter' | 'audience';

interface PresenterSyncState {
  idx: number;
  go: (n: number) => void;
}

export function usePresenterSync(
  slug: string,
  role: PresenterRole,
  initialIdx: number,
): PresenterSyncState {
  const [idx, setIdx] = useState(initialIdx);
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const channel = new BroadcastChannel(`${CHANNEL_PREFIX}${slug}`);
    channelRef.current = channel;

    if (role === 'audience') {
      channel.onmessage = (e: MessageEvent) => {
        if (e.data?.type === 'sn-presenter-nav' && typeof e.data.idx === 'number') {
          setIdx(e.data.idx);
        }
      };
    }

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [slug, role]);

  const go = useCallback(
    (n: number) => {
      setIdx(n);
      if (role === 'presenter' && channelRef.current) {
        channelRef.current.postMessage({ type: 'sn-presenter-nav', idx: n });
      }
    },
    [role],
  );

  return { idx, go };
}

/**
 * Open the audience window for the given deck slug.
 * Returns the window reference (or null if blocked by popup blocker).
 */
export function openAudienceWindow(slug: string): Window | null {
  const url = `/${slug}?audience=1#1`;
  return window.open(url, `sn-audience-${slug}`);
}
